import {
  IonButton,
  IonCheckbox,
  IonInput,
  IonItem,
  IonLabel,
  IonModal,
} from "@ionic/react";
import React from "react";
import clsx from "clsx";

import { Context } from "../../../../store/teacher";
import {
  IClass,
  ISubject,
  ISubjectCategory,
  ISubjectGearboxUsage,
  ITeacher,
  ITeacherGearbox,
  TNameStatus,
  TCoachType,
  TeacherGearboxDirectory,
  GearboxTemplate,
  CreateSubjectGearboxesParams,
} from "../../../../state";
import Toast from "../../../../molecules/Toast";
import ClassSelectForm from "../../../../molecules/ClassSelectForm";
import TeacherSelectForm from "../../../../molecules/TeacherSelectForm";
import SubjectSelector from "../../../../molecules/SubjectSelector";
import BoxTemplateSelector from "../../../../molecules/BoxTemplateSelector";
import BoxDirectorySelectForm from "../../../../molecules//BoxDirectorySelectForm";
import CoachSelector from "../../../../molecules/CoachSelector";
import ToolTip from "../../../../atoms/ToolTip";

import styles from "./BoxForm.module.scss";

const switchNameStatusKeyValue = (status: TNameStatus | boolean) => {
  const nameStatusKeyValue: { [key: string]: string | boolean } = {
    publish: true,
    unpublish: false,
    true: "publish",
    false: "unpublish",
  };
  return nameStatusKeyValue[`${status}`];
};

type TBoxKind = "normal" | "subject";

export interface BoxFormProps {
  calling: boolean;
  isOpen: boolean;
  classes: IClass[];
  teachers: ITeacher[];
  subjects: ISubject[];
  subjectCategories: ISubjectCategory[];
  subjectGearboxUsages: ISubjectGearboxUsage[];
  lastCreatedSubject: ISubject | null;
  teacherGearboxDirectories: TeacherGearboxDirectory[];
  defaultTitle: string;
  defaultTemplateId: number | null;
  id: number;
  classIDs: number[];
  teacherGearboxDirectoryIDs: number[];
  nameStatus: TNameStatus;
  is_submission_private: boolean;
  is_archived?: boolean;
  is_shared: boolean;
  subject_id: number | null;
  coach_types: TCoachType[];
  templates: GearboxTemplate[];
  gearboxes: ITeacherGearbox[];
  close: () => void;
  createOriginalSubject: (name: string) => void;
  updateOriginalSubject: (id: number, name: string) => void;
  deleteOriginalSubject: (id: number) => void;
  onCreateSubjectGearboxes: (params: CreateSubjectGearboxesParams) => void;
  ok: (box: {
    id: number;
    title: string;
    subject_id?: number;
    co_teacher_ids?: number[];
    class_ids: number[];
    teacher_gearbox_directory_ids: number[];
    name_status: TNameStatus;
    is_submission_private: boolean;
    is_archived: boolean;
    is_shared: boolean;
    is_hidden: boolean;
    gearbox_template_id: number | null;
    coach_types: TCoachType[];
  }) => void;
}

const BoxForm: React.FC<BoxFormProps> = ({
  calling,
  isOpen,
  classes,
  teachers,
  subjects,
  subjectCategories,
  subjectGearboxUsages,
  lastCreatedSubject,
  teacherGearboxDirectories,
  defaultTitle,
  defaultTemplateId,
  id,
  classIDs,
  teacherGearboxDirectoryIDs,
  nameStatus,
  is_submission_private,
  is_archived = false,
  is_shared,
  subject_id,
  coach_types,
  gearboxes,
  templates,
  close,
  createOriginalSubject,
  updateOriginalSubject,
  deleteOriginalSubject,
  onCreateSubjectGearboxes,
  ok,
}) => {
  const { contextState } = React.useContext(Context);

  const [state, setState] = React.useState<{
    boxKind: TBoxKind;
    showShareForm: {
      class: boolean;
      directory: boolean;
      subject: boolean;
      coTeacher: boolean;
    };
    showToast: boolean;
    message: string;
    title: string;
    selected_subject_ids: number[];
    selected_teacher_ids: number[];
    selected_class_ids: number[];
    selectedTeacherGearboxDirectoryIDs: number[];
    nameStatus: TNameStatus;
    is_submission_private: boolean;
    is_archived: boolean;
    is_shared: boolean;
    is_hidden: boolean;
    selected_template_id: number | null;
    selected_coach_types: TCoachType[];
  }>({
    boxKind: "normal",
    showShareForm: {
      class: false,
      directory: false,
      subject: false,
      coTeacher: false,
    },
    showToast: false,
    message: "",
    title: "",
    selected_subject_ids: [],
    selected_teacher_ids: [],
    selected_class_ids: [],
    selectedTeacherGearboxDirectoryIDs: [],
    nameStatus: "publish",
    is_submission_private: false,
    is_archived: false,
    is_shared: false,
    is_hidden: false,
    selected_template_id: defaultTemplateId,
    selected_coach_types: [],
  });

  const isSubjectCreation = !id && state.boxKind === "subject";
  const isSubjectEdit = !!id && !!subject_id;
  const isSubjectMode = isSubjectCreation || isSubjectEdit;
  const pendingOriginalSubject = React.useRef(false);

  const selected_template = templates?.find(
    (template: GearboxTemplate) => template.id === state.selected_template_id,
  );

  // 非共有BOXとして提出されたストックが公開されるのを防ぐため、
  // 保存済みの非共有BOXにストックがある場合はチェックを外せない
  const stockCount =
    gearboxes.find(gearbox => gearbox.id === id)?.realizations_count ?? 0;
  const isSubmissionPrivateLocked = is_submission_private && stockCount > 0;

  const update = React.useCallback(
    (args: { [key: string]: any }) => {
      if (!isSubjectMode) {
        const selected_class_ids =
          args.selected_class_ids ?? state.selected_class_ids;

        if (selected_class_ids.length > 1) {
          args.is_shared = true;
        }
        if (
          args.selected_class_ids?.length === 1 &&
          state.selected_class_ids.length === 2
        ) {
          args.is_shared = false;
        }
      }
      setState(prevState => ({ ...prevState, ...args }));
    },
    [state.selected_class_ids, isSubjectMode],
  );

  React.useEffect(() => {
    if (id) {
      const editingBox = gearboxes.find(gearbox => gearbox.id === id);
      const coTeacherIds =
        editingBox?.assigned_teachers
          ?.filter(t => t.id !== contextState.teacher.id)
          .map(t => t.id) ?? [];
      update({
        boxKind: "normal",
        title: defaultTitle,
        selected_class_ids: classIDs,
        selectedTeacherGearboxDirectoryIDs: teacherGearboxDirectoryIDs,
        nameStatus,
        is_submission_private,
        is_archived,
        is_shared,
        selected_template_id: defaultTemplateId,
        selected_coach_types: coach_types,
        selected_subject_ids: subject_id ? [subject_id] : [],
        selected_teacher_ids: coTeacherIds,
      });
    } else {
      const currentYearDirectoryId =
        teacherGearboxDirectories.find(
          directory =>
            directory.is_system &&
            directory.name === `${contextState.current_year}`,
        )?.id ?? null;

      update({
        boxKind: "normal",
        title: "",
        selected_subject_ids: [],
        selected_teacher_ids: [],
        selected_class_ids: [],
        selectedTeacherGearboxDirectoryIDs: currentYearDirectoryId
          ? [currentYearDirectoryId]
          : [],
        nameStatus: "publish",
        is_submission_private: false,
        is_archived: false,
        is_shared: false,
        selected_template_id: null,
        selected_coach_types: [],
        showShareForm: {
          class: false,
          directory: false,
          subject: false,
          coTeacher: false,
        },
      });
    }
    pendingOriginalSubject.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const selectedSubjectIds = React.useMemo(() => {
    if (subjects.length === 0) {
      return state.selected_subject_ids;
    }
    return state.selected_subject_ids.filter(subjectId =>
      subjects.some(subject => subject.id === subjectId),
    );
  }, [subjects, state.selected_subject_ids]);

  React.useEffect(() => {
    if (!id) return;

    // 現在のBOXに対応するディレクトリIDを取得
    const gearBox = gearboxes.find(gearbox => gearbox.id === id);
    const defaultDirectoryIDs = gearBox
      ? gearBox.teacher_gearbox_directory_ids
      : [];

    // ディレクトリIDを更新
    update({ selectedTeacherGearboxDirectoryIDs: defaultDirectoryIDs });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultSubjectTemplateId = React.useMemo(
    () =>
      templates.find(template => template.is_subject_box_default)?.id ?? null,
    [templates],
  );

  const switchBoxKind = (kind: TBoxKind) => {
    if (kind === state.boxKind) {
      return;
    }
    setState(prevState => ({
      ...prevState,
      boxKind: kind,
      title: "",
      selected_subject_ids: [],
      selected_teacher_ids: [],
      selected_class_ids: [],
      nameStatus: "publish",
      is_submission_private: false,
      is_archived: false,
      is_shared: false,
      selected_template_id:
        kind === "subject" ? defaultSubjectTemplateId : null,
      selected_coach_types: [],
      showShareForm: {
        class: false,
        directory: false,
        subject: false,
        coTeacher: false,
      },
    }));
  };

  // 選択中の教科×(自分x一緒に使う先生)で既にBOXがあるクラスは選択不可
  const disabledClassIds = React.useMemo(() => {
    if (!isSubjectMode || selectedSubjectIds.length === 0) {
      return [];
    }
    const targetTeacherIds = [
      contextState.teacher.id,
      ...state.selected_teacher_ids,
    ];
    return subjectGearboxUsages
      .filter(
        usage =>
          usage.gearbox_id !== id &&
          selectedSubjectIds.includes(usage.subject_id) &&
          targetTeacherIds.includes(usage.teacher_id),
      )
      .map(usage => usage.klass_id)
      .filter((klassId, index, self) => self.indexOf(klassId) === index);
  }, [
    isSubjectMode,
    id,
    selectedSubjectIds,
    state.selected_teacher_ids,
    subjectGearboxUsages,
    contextState.teacher.id,
  ]);

  const selectedClassIds = React.useMemo(() => {
    if (!isSubjectMode) {
      return state.selected_class_ids;
    }
    return state.selected_class_ids.filter(
      klassId => !disabledClassIds.includes(klassId),
    );
  }, [isSubjectMode, state.selected_class_ids, disabledClassIds]);

  React.useEffect(() => {
    if (!pendingOriginalSubject.current || !lastCreatedSubject) return;

    pendingOriginalSubject.current = false;
    setState(prevState =>
      prevState.selected_subject_ids.includes(lastCreatedSubject.id)
        ? prevState
        : {
            ...prevState,
            selected_subject_ids: [
              ...prevState.selected_subject_ids,
              lastCreatedSubject.id,
            ],
          },
    );
  }, [lastCreatedSubject]);

  const coTeacherCandidates = React.useMemo(
    () =>
      teachers.filter(
        teacher => teacher.serviced && teacher.id !== contextState.teacher.id,
      ),
    [teachers, contextState.teacher.id],
  );

  const disabledCoTeacherIds = React.useMemo(() => {
    if (
      !isSubjectMode ||
      selectedSubjectIds.length === 0 ||
      selectedClassIds.length === 0
    ) {
      return [];
    }
    return coTeacherCandidates
      .filter(
        teacher =>
          !state.selected_teacher_ids.includes(teacher.id) &&
          subjectGearboxUsages.some(
            usage =>
              usage.gearbox_id !== id &&
              usage.teacher_id === teacher.id &&
              selectedSubjectIds.includes(usage.subject_id) &&
              selectedClassIds.includes(usage.klass_id),
          ),
      )
      .map(teacher => teacher.id);
  }, [
    isSubjectMode,
    id,
    selectedSubjectIds,
    selectedClassIds,
    state.selected_teacher_ids,
    subjectGearboxUsages,
    coTeacherCandidates,
  ]);

  const disabledClassReasonByClassId = React.useMemo(() => {
    const reasons: { [klassId: number]: string } = {};
    if (disabledClassIds.length === 0) {
      return reasons;
    }
    const targetTeacherIds = [
      contextState.teacher.id,
      ...state.selected_teacher_ids,
    ];
    disabledClassIds.forEach(klassId => {
      const userNames = subjectGearboxUsages
        .filter(
          usage =>
            usage.gearbox_id !== id &&
            usage.klass_id === klassId &&
            selectedSubjectIds.includes(usage.subject_id) &&
            targetTeacherIds.includes(usage.teacher_id),
        )
        .map(usage => {
          if (usage.teacher_id === contextState.teacher.id) {
            return "自分";
          }
          const teacherName = teachers.find(
            teacher => teacher.id === usage.teacher_id,
          )?.full_name;
          return teacherName ? `${teacherName}先生` : "他の先生";
        })
        .filter((name, index, self) => self.indexOf(name) === index);
      reasons[klassId] = `${userNames.join(
        "、",
      )}が利用中の教科BOXがあるため選択できません`;
    });
    return reasons;
  }, [
    disabledClassIds,
    id,
    selectedSubjectIds,
    state.selected_teacher_ids,
    subjectGearboxUsages,
    teachers,
    contextState.teacher.id,
  ]);

  const onSubmit = React.useCallback(() => {
    if (subject_id && !state.title) {
      setState(s => ({
        ...s,
        showToast: true,
        message: "BOX名を入力してください。",
      }));
      return;
    }
    if (!subject_id && (!state.title || selectedClassIds.length === 0)) {
      setState(s => ({
        ...s,
        showToast: true,
        message: "BOX名とクラスを入力してください。",
      }));
      return;
    }
    ok({
      id: id || 0,
      title: state.title,
      class_ids: selectedClassIds,
      teacher_gearbox_directory_ids: state.selectedTeacherGearboxDirectoryIDs,
      name_status: state.nameStatus,
      is_submission_private:
        isSubmissionPrivateLocked || state.is_submission_private,
      is_archived: state.is_archived,
      is_shared: state.is_shared,
      is_hidden: state.is_hidden,
      gearbox_template_id: state.selected_template_id,
      coach_types: state.selected_coach_types,
    });
  }, [
    id,
    subject_id,
    state.title,
    selectedClassIds,
    state.selectedTeacherGearboxDirectoryIDs,
    state.nameStatus,
    isSubmissionPrivateLocked,
    state.is_submission_private,
    state.is_archived,
    state.is_shared,
    state.is_hidden,
    state.selected_template_id,
    state.selected_coach_types,
    ok,
  ]);

  const onSubmitSubjectEdit = React.useCallback(() => {
    if (selectedSubjectIds.length === 0 || selectedClassIds.length === 0) {
      setState(s => ({
        ...s,
        showToast: true,
        message: "教科とクラスを選択してください。",
      }));
      return;
    }
    if (selectedClassIds.length > 1) {
      setState(s => ({
        ...s,
        showToast: true,
        message: "クラスは1つ選択してください。",
      }));
      return;
    }
    ok({
      id,
      title: state.title,
      subject_id: selectedSubjectIds[0],
      co_teacher_ids: state.selected_teacher_ids,
      class_ids: selectedClassIds,
      teacher_gearbox_directory_ids: state.selectedTeacherGearboxDirectoryIDs,
      name_status: state.nameStatus,
      is_submission_private:
        isSubmissionPrivateLocked || state.is_submission_private,
      is_archived: state.is_archived,
      is_shared: false,
      is_hidden: state.is_hidden,
      gearbox_template_id: state.selected_template_id,
      coach_types: state.selected_coach_types,
    });
  }, [
    id,
    ok,
    isSubmissionPrivateLocked,
    state.title,
    selectedSubjectIds,
    state.selected_teacher_ids,
    selectedClassIds,
    state.selectedTeacherGearboxDirectoryIDs,
    state.nameStatus,
    state.is_submission_private,
    state.is_archived,
    state.is_hidden,
    state.selected_template_id,
    state.selected_coach_types,
  ]);

  const onSubmitSubject = React.useCallback(() => {
    if (selectedSubjectIds.length === 0 || selectedClassIds.length === 0) {
      setState(s => ({
        ...s,
        showToast: true,
        message: "教科とクラスを選択してください。",
      }));
      return;
    }
    onCreateSubjectGearboxes({
      subject_ids: selectedSubjectIds,
      class_ids: selectedClassIds,
      co_teacher_ids: state.selected_teacher_ids,
      teacher_gearbox_directory_ids: state.selectedTeacherGearboxDirectoryIDs,
      name_status: state.nameStatus,
      is_submission_private: state.is_submission_private,
      is_archived: state.is_archived,
      gearbox_template_id: state.selected_template_id,
      coach_types: state.selected_coach_types,
    });
  }, [
    onCreateSubjectGearboxes,
    selectedSubjectIds,
    selectedClassIds,
    state.selected_teacher_ids,
    state.selectedTeacherGearboxDirectoryIDs,
    state.nameStatus,
    state.is_submission_private,
    state.is_archived,
    state.selected_template_id,
    state.selected_coach_types,
  ]);

  const submissionPrivateCheckbox = (
    <IonCheckbox
      disabled={isSubmissionPrivateLocked}
      checked={state.is_submission_private}
      className={styles.checkbox}
      onIonChange={e => {
        update({ is_submission_private: e.detail.checked });
      }}
    />
  );

  return (
    <IonModal isOpen={isOpen} onDidDismiss={close} className={styles.wrapper}>
      <Toast
        type="danger"
        showToast={state.showToast}
        onClose={() => setState(s => ({ ...s, showToast: false, message: "" }))}
        message={state.message}
      />
      <div className={styles.form}>
        {id ? (
          <div className={styles.label}>
            {subject_id ? "教科BOXを編集" : "BOX名を編集"}
          </div>
        ) : (
          <div className={styles.kindToggle}>
            <button
              type="button"
              className={clsx(
                styles.kindToggleButton,
                state.boxKind === "normal" && styles.kindToggleButtonActive,
              )}
              onClick={() => switchBoxKind("normal")}
            >
              BOXをつくる
            </button>
            <button
              type="button"
              className={clsx(
                styles.kindToggleButton,
                state.boxKind === "subject" && styles.kindToggleButtonActive,
              )}
              onClick={() => switchBoxKind("subject")}
            >
              教科BOXをつくる
            </button>
          </div>
        )}
        {isSubjectCreation && (
          <p className={styles.kindDescription}>
            教科BOXでは、授業の振り返りに対して教科専用のAIフィードバックが表示されます
          </p>
        )}
        <div className={styles.sectionLabel}>必須項目</div>
        {isSubjectMode ? (
          <SubjectSelector
            isOpen={state.showShareForm.subject}
            subjects={subjects}
            subjectCategories={subjectCategories}
            selected_subject_ids={selectedSubjectIds}
            single={isSubjectEdit}
            calling={calling}
            className={styles.subjectSelector}
            onClick={() => {
              setState(s => ({
                ...s,
                showShareForm: {
                  ...s.showShareForm,
                  subject: !s.showShareForm.subject,
                },
              }));
            }}
            update={update}
            onCreateOriginalSubject={name => {
              pendingOriginalSubject.current = true;
              createOriginalSubject(name);
            }}
            onUpdateOriginalSubject={updateOriginalSubject}
            onDeleteOriginalSubject={deleteOriginalSubject}
          />
        ) : (
          <div className={styles.inputArea}>
            <IonInput
              className={styles.input}
              name="gearboxTitle"
              value={state.title}
              placeholder="BOX名を記入"
              required
              onIonChange={e => update({ title: e.detail.value })}
            />
          </div>
        )}
        {(isSubjectMode || !subject_id) && (
          <ClassSelectForm
            isOpen={state.showShareForm.class}
            label="クラスを選択"
            classes={classes}
            multiple
            groupByGrade
            singleSelect={isSubjectEdit}
            selected_class_ids={selectedClassIds}
            disabled_class_ids={isSubjectMode ? disabledClassIds : []}
            disabledReasonByClassId={
              isSubjectMode ? disabledClassReasonByClassId : undefined
            }
            className={styles.classSelectForm}
            whiteMode
            onClick={() => {
              setState({
                ...state,
                showShareForm: {
                  ...state.showShareForm,
                  class: !state.showShareForm.class,
                },
              });
            }}
            update={update}
          />
        )}
        <div className={styles.sectionLabel}>任意項目</div>
        {isSubjectMode && (
          <TeacherSelectForm
            isOpen={state.showShareForm.coTeacher}
            label="一緒に使う先生を選択"
            description="選択した先生とBOXを一緒に使えます。AIフィードバックは先生ごとに分かれず、まとめて分析されます。"
            teachers={coTeacherCandidates}
            selected_teacher_ids={state.selected_teacher_ids}
            disabled_teacher_ids={disabledCoTeacherIds}
            disabledReason="選択中の教科のBOXを既に利用しているため選択できません。対象のBOXを提出停止すると選択できるようになります"
            checkboxSelect
            whiteMode
            className={styles.teacherSelectForm}
            onClick={() => {
              setState(s => ({
                ...s,
                showShareForm: {
                  ...s.showShareForm,
                  coTeacher: !s.showShareForm.coTeacher,
                },
              }));
            }}
            update={update}
          />
        )}
        <div className={styles.template}>
          <BoxTemplateSelector
            key={`${state.boxKind}-${isOpen}`}
            defaultTemplateId={state.selected_template_id}
            templates={templates}
            handleChangeTemplate={(id: number | null) =>
              update({ selected_template_id: id })
            }
          />
          {selected_template && (
            <div className={styles.explanation}>
              <p className={styles.read}>
                生徒側の画面には、入力のサポートとして以下が表示されるようになります。
              </p>
              <p className={styles.text}>{selected_template.content}</p>
            </div>
          )}
        </div>
        <BoxDirectorySelectForm
          isOpen={state.showShareForm.directory}
          label="フォルダを選択"
          teacherGearboxDirectories={teacherGearboxDirectories}
          selectedTeacherGearboxDirectoryIDs={
            state.selectedTeacherGearboxDirectoryIDs
          }
          className={styles.boxDirectorySelectForm}
          onClick={() => {
            setState({
              ...state,
              showShareForm: {
                ...state.showShareForm,
                directory: !state.showShareForm.directory,
              },
            });
          }}
          update={update}
        />
        <CoachSelector
          selectedCoachTypes={state.selected_coach_types}
          onChange={coach_types =>
            update({ selected_coach_types: coach_types })
          }
        />
        <div className={styles.checkboxes}>
          <IonItem lines="none" className={styles.checkboxItem}>
            <IonCheckbox
              checked={!!switchNameStatusKeyValue(state.nameStatus)}
              className={styles.checkbox}
              onIonChange={e => {
                update({
                  nameStatus: switchNameStatusKeyValue(e.detail.checked),
                });
              }}
            />
            <IonLabel className={styles.checkboxLabel}>生徒名を公開</IonLabel>
          </IonItem>
          <IonItem lines="none" className={styles.checkboxItem}>
            {isSubmissionPrivateLocked ? (
              <ToolTip
                content="ストックが存在するため外すことができません。"
                placement="right"
              >
                {submissionPrivateCheckbox}
              </ToolTip>
            ) : (
              submissionPrivateCheckbox
            )}
            <IonLabel className={styles.checkboxLabel}>
              非共有BOX
              <span className={styles.supplement}>
                ※GEARタイムラインに表示されず、生徒同士で共有されないBOX
              </span>
            </IonLabel>
          </IonItem>
          <IonItem lines="none" className={styles.checkboxItem}>
            <IonCheckbox
              checked={state.is_archived}
              className={styles.checkbox}
              onIonChange={e => {
                update({ is_archived: e.detail.checked });
              }}
            />
            <IonLabel className={styles.checkboxLabel}>提出を停止</IonLabel>
          </IonItem>
          {!subject_id && !isSubjectCreation && (
            <IonItem lines="none" className={styles.checkboxItem}>
              <IonCheckbox
                disabled={state.selected_class_ids.length > 1}
                checked={state.is_shared}
                className={styles.checkbox}
                onIonChange={e => {
                  // 選択クラス数が2から1にする際にis_sharedが更新され、
                  // 不要なupdateが実行されるため選択クラス数が2より小さいときのみupdateを実行する
                  state.selected_class_ids.length < 2 &&
                    update({ is_shared: e.detail.checked });
                }}
              />
              <IonLabel className={styles.checkboxLabel}>
                選択クラスの他の担当先生にも表示
              </IonLabel>
            </IonItem>
          )}
        </div>
        <div className={styles.buttonArea}>
          <IonButton
            className={styles.cancelButton}
            strong
            fill="outline"
            onClick={close}
          >
            キャンセル
          </IonButton>
          <IonButton
            className={styles.postButton}
            disabled={calling}
            strong
            onClick={
              isSubjectCreation
                ? onSubmitSubject
                : isSubjectEdit
                ? onSubmitSubjectEdit
                : onSubmit
            }
          >
            保存
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
};

export default BoxForm;
